import axios from "axios";
import { chunk } from "lodash";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiSuffixType,
  BookingApi,
  BOOKINGS_PREPARE_CHUNK_SIZE,
  castErrorResult,
  classifyServerSearchPayload,
  companyIdSelector,
  ConfirmResult,
  createSuccessResult,
  CrudApi,
  errors,
  filterCriteriaSF,
  isTranslatableItem,
  Log,
  normalizeErrors,
  passengersListBookingAtom,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  transformApiCrudErrorForToast,
  translateInfoItem,
  type BookingEntity,
  type IApiError,
  type IBookingPrepareResult,
  type IBulkPrepareBookingPayload,
  type IExtendableDto,
  type IPaginatedGridSearchPayload,
  type IPassengerDto,
  type IPassengerGetStationsDto,
  type ISearchCriteria,
  type ISearchPassengerStations,
  type ITypeormWhereCriteria,
  type PassengerEntity,
  type SearchQueryForOptimize,
} from "@ryde/shared";

import { useModalConfirm } from "../../hooks-singletons";
import { useIsFetchAllowed } from "../use-is-fetch-allowed";
import { buildBookingPassengersWhere } from "./use-bookings-actions.utils";
import { useBookingsListActions } from "./use-bookings-list-actions";

interface IUseBookingsActionsArguments {
  searchQueryToOptimize?: SearchQueryForOptimize;
}

export const useBookingsActions = (args?: IUseBookingsActionsArguments) => {
  const { searchQueryToOptimize } = args ?? {};
  const companyId = useRecoilValue(companyIdSelector);
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const setBookingPassengers = useSetRecoilState(passengersListBookingAtom);
  const bookingFiltersSchemaObj:
    ITypeormWhereCriteria<BookingEntity> | undefined = useRecoilValue(
    filterCriteriaSF("booking"),
  );

  const { removeBooking } = useBookingsListActions();
  const { confirmDialogResult } = useModalConfirm();
  const { getIsFetchAllowed } = useIsFetchAllowed({ searchQueryToOptimize });
  const { t } = useTranslation();

  const archiveBooking = useEvent(async (id: number): Promise<boolean> => {
    try {
      const confirmationResult = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t("booking.ARCHIVE_SELECTED_BOOKING"),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        Log.warnAndToast(t("errors.bookingArchiveCanceledByUser"));
        return false;
      }

      setLoading(true);
      await CrudApi.delete("booking", +id);
      removeBooking(+id);
      return true;
    } catch (err) {
      let errorsNormalized = normalizeErrors(err as IApiError, t);

      if (!errorsNormalized?.length) {
        errorsNormalized = [t("errors.crudAPIError")];
      }

      Log.warnAndToast(
        transformApiCrudErrorForToast({ errorsNormalized, tFunction: t }),
      );
      setErrors(prevErrors => [...(prevErrors || []), ...errorsNormalized]);

      return false;
    } finally {
      setLoading(false);
    }
  });

  const fetchBookingPassengers = useEvent(async (): Promise<boolean> => {
    const cancelTokenSource = axios.CancelToken.source();

    try {
      if (!companyId) {
        Log.errorAndToast(t("errors.invalidCompanyId"));
        return false;
      }

      setErrors(undefined);
      setLoading(true);

      Log.debug(
        "Booking filter schema for passengers criteria extraction: ",
        bookingFiltersSchemaObj,
      );

      const passengerGeneratedFilterSchema = buildBookingPassengersWhere(
        bookingFiltersSchemaObj,
      );

      const body: ISearchCriteria<PassengerEntity> = {
        where: passengerGeneratedFilterSchema,
      };

      const isFetchAllowed = await getIsFetchAllowed(body);

      if (!isFetchAllowed) {
        return false;
      }

      Log.debug(
        `Fetch passengers for bookings for company id: ${companyId}, schema:`,
        passengerGeneratedFilterSchema,
      );

      const passengersSearchResult = await CrudApi.post<
        SimpleResult<IPaginatedGridSearchPayload<IExtendableDto>>
      >({
        entity: "passenger",
        data: body,
        suffixType: ApiSuffixType.ServerSearch,
        cancelToken: cancelTokenSource.token,
      });

      if (
        !passengersSearchResult ||
        (passengersSearchResult.success && !passengersSearchResult.data)
      ) {
        setErrors([t(errors.common.emptyApiResponse)]);
        return false;
      }

      if (!passengersSearchResult.success) {
        const error = passengersSearchResult.error;

        if (isTranslatableItem(error)) {
          setErrors([translateInfoItem(error, t)]);
        } else {
          setErrors([t(errors.ride.failedFetchingRides)]);
        }

        return false;
      }

      const parsed = classifyServerSearchPayload(passengersSearchResult.data);

      if (!parsed) {
        setErrors([t(errors.common.emptyApiResponse)]);
        return false;
      }

      const dtos =
        parsed.kind === "groups"
          ? parsed.groups.flatMap(group => group.groupData ?? [])
          : parsed.items;

      Log.debug("Passengers for bookings dto received: ", dtos);
      setBookingPassengers(dtos as IPassengerDto[]);
      return true;
    } catch (error) {
      if (axios.isCancel(error)) {
        Log.debug("Axios, request canceled", error);
        return false;
      }

      Log.errorAndToast(t("errors.failedToFetchPassengersForBooking"), error);
      setErrors(normalizeErrors(error as IApiError, t));

      return false;
    } finally {
      setLoading(false);
    }
  });

  const searchPassengerStations = useEvent(
    async (
      payload: ISearchPassengerStations,
    ): Promise<SimpleResult<IPassengerGetStationsDto[]>> => {
      try {
        setLoading(true);
        const passengerStationsResult =
          await BookingApi.searchPassengerStations(payload);

        if (
          !passengerStationsResult?.success ||
          !passengerStationsResult.data
        ) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        Log.debug(
          "Search Passenger Stations dto received: ",
          passengerStationsResult.data,
        );
        return new SimpleResult({
          success: true,
          data: passengerStationsResult.data,
        });
      } catch (error) {
        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setLoading(false);
      }
    },
  );

  const bookingsPrepare = useEvent(
    async (
      payload: IBulkPrepareBookingPayload,
    ): Promise<SimpleResult<IBookingPrepareResult>> => {
      try {
        setLoading(true);
        const items = payload.data ?? [];
        const chunks = chunk(items, BOOKINGS_PREPARE_CHUNK_SIZE);
        const result: IBookingPrepareResult = {
          data: [],
          conflicts: [],
        };

        for (let i = 0; i < chunks.length; i += 1) {
          const chunkPayload: IBulkPrepareBookingPayload = {
            data: chunks[i],
            entityCreateFields: payload.entityCreateFields,
          };
          const bookingPrepareResult = await BookingApi.bookingsPrepare({
            body: chunkPayload,
          });

          if (!bookingPrepareResult) {
            return simpleResultErrorEmptyResponse(t);
          }

          if (!bookingPrepareResult.success || !bookingPrepareResult.data) {
            return castErrorResult(bookingPrepareResult);
          }

          result.data.push(...(bookingPrepareResult.data.data ?? []));
          result.conflicts.push(...(bookingPrepareResult.data.conflicts ?? []));
        }

        Log.debug("Bookings Prepare dto received: ", result);

        return createSuccessResult(result);
      } catch (error) {
        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    archiveBooking,
    fetchBookingPassengers,
    searchPassengerStations,
    bookingsPrepare,
  };
};
